import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { pedir } from '../api/cliente';
import { Boton, Encabezado, Insignia, Tarjeta } from '../componentes/ui/basicos';
import { Cargando, Problema, Vacio } from '../componentes/ui/estados';
import { bajarPlanilla } from '../lib/planilla';
import { conDosDecimales } from '../venta/PiezasDeVenta';

type QueLibro = 'diario' | 'mayor' | 'sumas' | 'resultados';

interface RenglonDelDiario {
  readonly cuentaCodigo: string;
  readonly cuentaNombre: string;
  readonly debe: string;
  readonly haber: string;
}

interface AsientoDelDiario {
  readonly id: string;
  readonly numero: string;
  readonly fecha: string;
  readonly tipo: string;
  readonly glosa: string;
  readonly renglones: readonly RenglonDelDiario[];
}

interface FilaDeSumasYSaldos {
  readonly codigo: string;
  readonly nombre: string;
  readonly debe: string;
  readonly haber: string;
  readonly saldoDeudor: string;
  readonly saldoAcreedor: string;
}

interface SumasYSaldos {
  readonly filas: readonly FilaDeSumasYSaldos[];
  readonly totales: {
    readonly debe: string;
    readonly haber: string;
    readonly saldoDeudor: string;
    readonly saldoAcreedor: string;
  };
  readonly cuadra: boolean;
}

interface RenglonDelMayor {
  readonly asientoId: string;
  readonly numero: string;
  readonly fecha: string;
  readonly glosa: string;
  readonly debe: string;
  readonly haber: string;
  readonly acumulado: string;
}

interface MayorDeCuenta {
  readonly cuenta: { readonly codigo: string; readonly nombre: string };
  readonly saldoAnterior: string;
  readonly renglones: readonly RenglonDelMayor[];
  readonly saldoFinal: string;
}

interface RenglonDeResultados {
  readonly codigo: string;
  readonly nombre: string;
  readonly importe: string;
}

interface EstadoDeResultados {
  readonly positivos: readonly RenglonDeResultados[];
  readonly negativos: readonly RenglonDeResultados[];
  readonly totalPositivos: string;
  readonly totalNegativos: string;
  readonly resultado: string;
}

interface CuentaALaVista {
  readonly id: string;
  readonly codigo: string;
  readonly nombre: string;
  readonly hijas: number;
}

/** El primer día del año en curso. Es el arranque del ejercicio en la enorme mayoría de los casos. */
function comienzoDelAno(): string {
  return `${String(new Date().getFullYear())}-01-01`;
}

function hoy(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Los libros contables.
 *
 * Cuatro libros en una pantalla y no cuatro pantallas, porque **se piden con el mismo período y se
 * miran uno después del otro**: quien revisa un mes abre el diario, ve un número raro, va al mayor
 * de esa cuenta y vuelve. Cuatro direcciones distintas obligarían a reescribir las fechas cada vez.
 *
 * Dos cosas que la pantalla hace y que no son adorno:
 *
 * - **El período arranca en el 1 de enero.** No es una suposición inocente —es lo que casi siempre
 *   se quiere—, pero está a la vista y se cambia. Lo que no se puede es que el libro salga sin
 *   fechas: la ruta las exige, justamente para que lo que se archive se pueda volver a pedir igual.
 * - **Se imprime y se baja.** Un libro contable termina en papel o en el correo del estudio. La
 *   impresión sale de la misma pantalla con `print:` y la planilla se arma con lo que ya está acá,
 *   así que lo que se baja es exactamente lo que se está mirando.
 */
export function Libros(): React.ReactElement {
  const { t } = useTranslation();
  const [cual, setCual] = useState<QueLibro>('diario');
  const [desde, setDesde] = useState(comienzoDelAno);
  const [hasta, setHasta] = useState(hoy);
  const [cuentaId, setCuentaId] = useState('');

  const periodo = `desde=${desde}&hasta=${hasta}`;

  const cuentas = useQuery({
    queryKey: ['cuentas-contables'],
    queryFn: () => pedir<CuentaALaVista[]>('/cuentas-contables'),
  });

  const diario = useQuery({
    queryKey: ['libro-diario', desde, hasta],
    enabled: cual === 'diario',
    queryFn: () => pedir<AsientoDelDiario[]>(`/libros/diario?${periodo}`),
  });

  const sumas = useQuery({
    queryKey: ['libro-sumas', desde, hasta],
    enabled: cual === 'sumas',
    queryFn: () => pedir<SumasYSaldos>(`/libros/sumas-y-saldos?${periodo}`),
  });

  const resultados = useQuery({
    queryKey: ['libro-resultados', desde, hasta],
    enabled: cual === 'resultados',
    queryFn: () => pedir<EstadoDeResultados>(`/libros/estado-de-resultados?${periodo}`),
  });

  const mayor = useQuery({
    queryKey: ['libro-mayor', cuentaId, desde, hasta],
    enabled: cual === 'mayor' && cuentaId !== '',
    queryFn: () => pedir<MayorDeCuenta>(`/libros/mayor/${cuentaId}?${periodo}`),
  });

  /** Sólo las imputables: pedir el mayor de un rubro devolvería un libro vacío y confundiría. */
  const imputables = (cuentas.data ?? []).filter((cuenta) => cuenta.hijas === 0);

  function bajar(): void {
    const sufijo = `${desde}_${hasta}`;
    if (cual === 'diario' && diario.data !== undefined) {
      bajarPlanilla(
        `diario_${sufijo}`,
        [
          t('libros.numero'),
          t('libros.fecha'),
          t('libros.glosa'),
          t('libros.cuenta'),
          t('libros.debe'),
          t('libros.haber'),
        ],
        diario.data.flatMap((asiento) =>
          asiento.renglones.map((renglon) => [
            asiento.numero,
            asiento.fecha.slice(0, 10),
            asiento.glosa,
            `${renglon.cuentaCodigo} ${renglon.cuentaNombre}`,
            renglon.debe,
            renglon.haber,
          ]),
        ),
      );
    } else if (cual === 'sumas' && sumas.data !== undefined) {
      bajarPlanilla(
        `sumas_y_saldos_${sufijo}`,
        [
          t('libros.cuenta'),
          t('libros.nombre'),
          t('libros.debe'),
          t('libros.haber'),
          t('libros.saldoDeudor'),
          t('libros.saldoAcreedor'),
        ],
        sumas.data.filas.map((fila) => [
          fila.codigo,
          fila.nombre,
          fila.debe,
          fila.haber,
          fila.saldoDeudor,
          fila.saldoAcreedor,
        ]),
      );
    } else if (cual === 'resultados' && resultados.data !== undefined) {
      bajarPlanilla(
        `estado_de_resultados_${sufijo}`,
        [t('libros.cuenta'), t('libros.nombre'), t('libros.importe')],
        [...resultados.data.positivos, ...resultados.data.negativos].map((fila) => [
          fila.codigo,
          fila.nombre,
          fila.importe,
        ]),
      );
    } else if (cual === 'mayor' && mayor.data !== undefined) {
      bajarPlanilla(
        `mayor_${mayor.data.cuenta.codigo}_${sufijo}`,
        [
          t('libros.numero'),
          t('libros.fecha'),
          t('libros.glosa'),
          t('libros.debe'),
          t('libros.haber'),
          t('libros.acumulado'),
        ],
        mayor.data.renglones.map((renglon) => [
          renglon.numero,
          renglon.fecha.slice(0, 10),
          renglon.glosa,
          renglon.debe,
          renglon.haber,
          renglon.acumulado,
        ]),
      );
    }
  }

  const enCurso =
    (cual === 'diario' && diario.isPending) ||
    (cual === 'sumas' && sumas.isPending) ||
    (cual === 'resultados' && resultados.isPending) ||
    (cual === 'mayor' && cuentaId !== '' && mayor.isPending);

  const problema = diario.error ?? sumas.error ?? resultados.error ?? mayor.error;

  return (
    <section className="flex flex-col gap-4">
      <Encabezado
        titulo={t('libros.titulo')}
        descripcion={t('libros.descripcion')}
        acciones={
          <div className="flex gap-2 print:hidden">
            <Boton onClick={bajar}>{t('libros.planilla')}</Boton>
            <Boton
              tono="principal"
              onClick={() => {
                globalThis.print();
              }}
            >
              {t('acciones.imprimir')}
            </Boton>
          </div>
        }
      />

      {/* El período y qué libro. Va arriba de todo y no se esconde: es lo que define qué dice
          cualquier número de abajo, y un libro del que no se ve el período no se puede archivar. */}
      <Tarjeta>
        <div className="flex flex-wrap items-end gap-3">
          <label className="flex flex-col gap-1 text-xs text-texto-tenue">
            {t('libros.desde')}
            <input
              type="date"
              value={desde}
              onChange={(evento) => {
                setDesde(evento.target.value);
              }}
              className="rounded-marco border border-borde bg-superficie px-2 py-1 text-sm text-texto"
            />
          </label>
          <label className="flex flex-col gap-1 text-xs text-texto-tenue">
            {t('libros.hasta')}
            <input
              type="date"
              value={hasta}
              onChange={(evento) => {
                setHasta(evento.target.value);
              }}
              className="rounded-marco border border-borde bg-superficie px-2 py-1 text-sm text-texto"
            />
          </label>

          <div className="flex gap-1 print:hidden">
            {(['diario', 'mayor', 'sumas', 'resultados'] as const).map((cadaUno) => (
              <Boton
                key={cadaUno}
                tono={cual === cadaUno ? 'principal' : 'secundario'}
                onClick={() => {
                  setCual(cadaUno);
                }}
              >
                {t(`libros.libro.${cadaUno}`)}
              </Boton>
            ))}
          </div>

          {cual === 'mayor' ? (
            <label className="flex flex-col gap-1 text-xs text-texto-tenue">
              {t('libros.cuenta')}
              <select
                value={cuentaId}
                onChange={(evento) => {
                  setCuentaId(evento.target.value);
                }}
                className="rounded-marco border border-borde bg-superficie px-2 py-1 text-sm text-texto"
              >
                <option value="">{t('libros.elegirCuenta')}</option>
                {imputables.map((cuenta) => (
                  <option key={cuenta.id} value={cuenta.id}>
                    {cuenta.codigo} {cuenta.nombre}
                  </option>
                ))}
              </select>
            </label>
          ) : null}
        </div>
      </Tarjeta>

      {problema != null ? <Problema error={problema} /> : null}
      {enCurso ? <Cargando /> : null}

      {cual === 'diario' && diario.data !== undefined ? (
        <Tarjeta>
          <h2 className="mb-3 text-sm font-semibold">
            {t('libros.libro.diario')} · {desde} — {hasta}
          </h2>
          {diario.data.length === 0 ? (
            <Vacio titulo={t('libros.vacio')} />
          ) : (
            <div className="flex flex-col gap-4">
              {diario.data.map((asiento) => (
                <div key={asiento.id} className="break-inside-avoid">
                  <p className="text-xs text-texto-tenue">
                    <span className="numero">{asiento.numero}</span> ·{' '}
                    {new Date(asiento.fecha).toLocaleDateString()} ·{' '}
                    {t(`asientos.tipos.${asiento.tipo}`)} · {asiento.glosa}
                  </p>
                  <table className="w-full text-sm">
                    <tbody>
                      {asiento.renglones.map((renglon, indice) => (
                        <tr
                          key={`${asiento.id}-${String(indice)}`}
                          className="border-t border-borde"
                        >
                          <td className="py-1">
                            <span className="numero text-xs text-texto-tenue">
                              {renglon.cuentaCodigo}
                            </span>{' '}
                            {renglon.cuentaNombre}
                          </td>
                          <td className="w-32 py-1 text-right numero">
                            {Number(renglon.debe) === 0 ? '' : conDosDecimales(renglon.debe)}
                          </td>
                          <td className="w-32 py-1 text-right numero">
                            {Number(renglon.haber) === 0 ? '' : conDosDecimales(renglon.haber)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ))}
            </div>
          )}
        </Tarjeta>
      ) : null}

      {cual === 'mayor' ? (
        <Tarjeta>
          {cuentaId === '' || mayor.data === undefined ? (
            <Vacio titulo={t('libros.elegirCuenta')} />
          ) : (
            <>
              <h2 className="mb-3 text-sm font-semibold">
                <span className="numero">{mayor.data.cuenta.codigo}</span>{' '}
                {mayor.data.cuenta.nombre} · {desde} — {hasta}
              </h2>
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-texto-tenue">
                    <th className="pb-1">{t('libros.numero')}</th>
                    <th className="pb-1">{t('libros.glosa')}</th>
                    <th className="pb-1 text-right">{t('libros.debe')}</th>
                    <th className="pb-1 text-right">{t('libros.haber')}</th>
                    <th className="pb-1 text-right">{t('libros.acumulado')}</th>
                  </tr>
                </thead>
                <tbody>
                  {/* El saldo anterior es la primera línea del libro y no un dato al costado: es
                      con lo que la cuenta arranca, y sin verlo el acumulado no significa nada. */}
                  <tr className="border-t border-borde text-texto-tenue">
                    <td className="py-1" colSpan={4}>
                      {t('libros.saldoAnterior')}
                    </td>
                    <td className="py-1 text-right numero">
                      {conDosDecimales(mayor.data.saldoAnterior)}
                    </td>
                  </tr>
                  {mayor.data.renglones.map((renglon, indice) => (
                    <tr
                      key={`${renglon.asientoId}-${String(indice)}`}
                      className="border-t border-borde"
                    >
                      <td className="py-1 numero">{renglon.numero}</td>
                      <td className="py-1">{renglon.glosa}</td>
                      <td className="py-1 text-right numero">
                        {Number(renglon.debe) === 0 ? '' : conDosDecimales(renglon.debe)}
                      </td>
                      <td className="py-1 text-right numero">
                        {Number(renglon.haber) === 0 ? '' : conDosDecimales(renglon.haber)}
                      </td>
                      <td className="py-1 text-right numero">
                        {conDosDecimales(renglon.acumulado)}
                      </td>
                    </tr>
                  ))}
                  <tr className="border-t-2 border-borde font-semibold">
                    <td className="py-1" colSpan={4}>
                      {t('libros.saldoFinal')}
                    </td>
                    <td className="py-1 text-right numero">
                      {conDosDecimales(mayor.data.saldoFinal)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </>
          )}
        </Tarjeta>
      ) : null}

      {cual === 'sumas' && sumas.data !== undefined ? (
        <Tarjeta>
          <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold">
            {t('libros.libro.sumas')} · {desde} — {hasta}
            {/* Que cuadre es lo primero que mira quien lo recibe. Va acá y no al final de una
                columna de números que hay que sumar de cabeza. */}
            <Insignia tono={sumas.data.cuadra ? 'bien' : 'mal'}>
              {sumas.data.cuadra ? t('libros.cuadra') : t('libros.noCuadra')}
            </Insignia>
          </h2>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-texto-tenue">
                <th className="pb-1">{t('libros.cuenta')}</th>
                <th className="pb-1 text-right">{t('libros.debe')}</th>
                <th className="pb-1 text-right">{t('libros.haber')}</th>
                <th className="pb-1 text-right">{t('libros.saldoDeudor')}</th>
                <th className="pb-1 text-right">{t('libros.saldoAcreedor')}</th>
              </tr>
            </thead>
            <tbody>
              {sumas.data.filas.map((fila) => (
                <tr key={fila.codigo} className="border-t border-borde">
                  <td className="py-1">
                    <span className="numero text-xs text-texto-tenue">{fila.codigo}</span>{' '}
                    {fila.nombre}
                  </td>
                  <td className="py-1 text-right numero">{conDosDecimales(fila.debe)}</td>
                  <td className="py-1 text-right numero">{conDosDecimales(fila.haber)}</td>
                  <td className="py-1 text-right numero">
                    {Number(fila.saldoDeudor) === 0 ? '' : conDosDecimales(fila.saldoDeudor)}
                  </td>
                  <td className="py-1 text-right numero">
                    {Number(fila.saldoAcreedor) === 0 ? '' : conDosDecimales(fila.saldoAcreedor)}
                  </td>
                </tr>
              ))}
              <tr className="border-t-2 border-borde font-semibold">
                <td className="py-1">{t('libros.totales')}</td>
                <td className="py-1 text-right numero">
                  {conDosDecimales(sumas.data.totales.debe)}
                </td>
                <td className="py-1 text-right numero">
                  {conDosDecimales(sumas.data.totales.haber)}
                </td>
                <td className="py-1 text-right numero">
                  {conDosDecimales(sumas.data.totales.saldoDeudor)}
                </td>
                <td className="py-1 text-right numero">
                  {conDosDecimales(sumas.data.totales.saldoAcreedor)}
                </td>
              </tr>
            </tbody>
          </table>
        </Tarjeta>
      ) : null}

      {cual === 'resultados' && resultados.data !== undefined ? (
        <Tarjeta>
          <h2 className="mb-3 text-sm font-semibold">
            {t('libros.libro.resultados')} · {desde} — {hasta}
          </h2>
          <table className="w-full text-sm">
            <tbody>
              <tr>
                <th colSpan={2} className="pb-1 text-left text-xs text-texto-tenue">
                  {t('libros.positivos')}
                </th>
              </tr>
              {resultados.data.positivos.map((fila) => (
                <tr key={fila.codigo} className="border-t border-borde">
                  <td className="py-1">
                    <span className="numero text-xs text-texto-tenue">{fila.codigo}</span>{' '}
                    {fila.nombre}
                  </td>
                  <td className="w-40 py-1 text-right numero">{conDosDecimales(fila.importe)}</td>
                </tr>
              ))}
              <tr className="border-t border-borde font-medium">
                <td className="py-1">{t('libros.totalPositivos')}</td>
                <td className="py-1 text-right numero">
                  {conDosDecimales(resultados.data.totalPositivos)}
                </td>
              </tr>

              <tr>
                <th colSpan={2} className="pt-4 pb-1 text-left text-xs text-texto-tenue">
                  {t('libros.negativos')}
                </th>
              </tr>
              {resultados.data.negativos.map((fila) => (
                <tr key={fila.codigo} className="border-t border-borde">
                  <td className="py-1">
                    <span className="numero text-xs text-texto-tenue">{fila.codigo}</span>{' '}
                    {fila.nombre}
                  </td>
                  <td className="py-1 text-right numero">{conDosDecimales(fila.importe)}</td>
                </tr>
              ))}
              <tr className="border-t border-borde font-medium">
                <td className="py-1">{t('libros.totalNegativos')}</td>
                <td className="py-1 text-right numero">
                  {conDosDecimales(resultados.data.totalNegativos)}
                </td>
              </tr>

              {/* Ganancia o pérdida, dicho con palabras. Un número con signo obliga a acordarse de
                  qué quiere decir el menos, y es lo único que quien lo mira quiere saber. */}
              <tr className="border-t-2 border-borde font-semibold">
                <td className="py-1">
                  {Number(resultados.data.resultado) < 0
                    ? t('libros.perdida')
                    : t('libros.ganancia')}
                </td>
                <td className="py-1 text-right numero">
                  {conDosDecimales(resultados.data.resultado)}
                </td>
              </tr>
            </tbody>
          </table>
        </Tarjeta>
      ) : null}
    </section>
  );
}
